<script setup lang="ts">
  import type { AvatarProps } from 'naive-ui'

  import { useComponentConfig } from '@/shared/lib/tenant'
  import { AvatarIcon, ProfileAvatarIcon } from '@/shared/ui/svg'

  type UserAvatarSize = number | `${number}px` | NonNullable<AvatarProps['size']>

  const props = withDefaults(
    defineProps<{
      color?: string
      iconVariant?: 'default' | 'profile'
      loading?: boolean
      radius?: string
      size: UserAvatarSize
      src?: string
    }>(),
    {
      iconVariant: 'default',
      loading: false,
      radius: '50%',
    },
  )

  const { componentsConfig } = useComponentConfig()
  const defaultAvatar = computed(() => componentsConfig.value?.profiles?.profilesDefaultAvatarImage)

  const avatarSize = computed<NonNullable<AvatarProps['size']>>(() => {
    if (typeof props.size === 'number') {
      return props.size
    }
    if (typeof props.size === 'string' && props.size.endsWith('px')) {
      return Number.parseInt(props.size, 10)
    }
    return props.size as NonNullable<AvatarProps['size']>
  })

  const contentSize = computed<number | undefined>(() => {
    if (typeof avatarSize.value === 'number') {
      return avatarSize.value
    }
    return undefined
  })

  const avatar = computed(() => {
    if (props.loading) {
      return { component: 'skeleton' as const }
    }
    if (props.src) {
      return { component: 'img' as const, src: props.src }
    }
    if (defaultAvatar.value) {
      return { component: 'img' as const, src: defaultAvatar.value }
    }
    return { component: 'icon' as const }
  })

  const isSkeletonRound = computed(() => props.radius === '50%')
</script>

<template>
  <NAvatar
    class="n-avatar-fix"
    lazy
    :size="avatarSize"
    :theme-overrides="{
      borderRadius: radius,
    }"
  >
    <NSkeleton
      v-if="avatar.component === 'skeleton'"
      :round="isSkeletonRound"
      :height="contentSize"
      :width="contentSize"
    />
    <img
      v-else-if="avatar.component === 'img'"
      :src="avatar.src"
      :style="{
        width: `${contentSize}px`,
        height: `${contentSize}px`,
      }"
      alt=""
      class="max-w-none object-cover"
    />
    <ProfileAvatarIcon
      v-else-if="iconVariant === 'profile'"
      :height="contentSize"
      :width="contentSize"
      :color="color"
    />
    <AvatarIcon
      v-else
      class="aspect-210/297"
      :height="contentSize"
      :width="contentSize"
    />
  </NAvatar>
</template>
